import { useTranslation } from "react-i18next";
import { isRangeValue, type AttributeDefView, type AttributeValue, type RangeValue } from "../api/catalog";
import { DateInput } from "./DateInput";
import { Select, type SelectOption } from "./Select";
import { InfoPopover } from "./TextPopover";

/**
 * Dynamic form over the attribute definitions of a type. Used for the actual
 * values of an asset (`/asseti/:id`) and the reference values of a position
 * (`/struktura/:id`) — both are `Record<key, value>` validated server-side
 * against the same `AttributeDefView[]`.
 */
export function AttributeValuesForm({
  defs, values, disabled, onChange, errors,
}: {
  defs: AttributeDefView[];
  values: Record<string, AttributeValue>;
  disabled: boolean;
  onChange: (next: Record<string, AttributeValue>) => void;
  /** Attribute key → message from the server (`details.key` of a 400); shown under that field. */
  errors?: Record<string, string>;
}) {
  return (
    <div className="form-grid">
      {defs.map((d) => (
        <AttributeInput
          key={d.id}
          def={d}
          value={values[d.key] ?? null}
          disabled={disabled}
          onChange={(v) => onChange({ ...values, [d.key]: v })}
          error={errors?.[d.key]}
        />
      ))}
    </div>
  );
}

/** Empty for the form's purposes: null, "", or a span with neither end. */
export function isEmptyAttributeValue(v: AttributeValue | undefined): boolean {
  if (v === null || v === undefined || v === "") return true;
  return isRangeValue(v) && v.min === null && v.max === null;
}

/** Required definitions without a value — the form cannot be saved while any remain. */
export function missingRequiredAttributes(defs: AttributeDefView[], values: Record<string, AttributeValue>): AttributeDefView[] {
  return defs.filter((d) => d.required && isEmptyAttributeValue(values[d.key]));
}

/** Drops empty entries — values are replaced whole on PUT, so only real ones are sent. */
export function compactAttributeValues(values: Record<string, AttributeValue>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(values)) {
    if (isEmptyAttributeValue(v)) continue;
    out[k] = v;
  }
  return out;
}

export function AttributeInput({
  def, value, disabled, onChange, error,
}: {
  def: AttributeDefView;
  value: AttributeValue;
  disabled: boolean;
  onChange: (v: AttributeValue) => void;
  /** Server-side complaint about this value; shown under the field, the box turns red. */
  error?: string;
}) {
  const { t } = useTranslation();
  // An attribute with a note (sprint 5f, V29.4) gets an "i" next to its label; the note opens in a popover.
  const note = def.note ? <InfoPopover text={def.note} label={def.label} /> : null;
  const label = (
    <span className="field-label">
      {def.label}
      {def.required && <span className="req-mark" title={t("common.required", "Obavezno")}>*</span>}
      {note}
    </span>
  );
  const errorNode = error ? <small className="field-error" role="alert">{error}</small> : null;
  const inputClass = error ? "input is-invalid" : "input";

  if (def.dataType === "BOOLEAN") {
    return (
      <label className="check-row" style={{ alignSelf: "end" }}>
        <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
        {def.label}{def.required && <span className="req-mark">*</span>}
        {note}
        {errorNode}
      </label>
    );
  }

  if (def.dataType === "ENUM") {
    // The list offers the values (a description is searchable but not shown in the row, it can be long);
    // the description of the picked value stands under the field, in full (Strahinja, 2026-10-09).
    const options: SelectOption<string>[] = [
      { value: "", label: t("assetTypes.editor.enumNone", "— nije uneto —") },
      ...(def.enumOptions ?? []).map((o) => ({ value: o.value, label: o.value, keywords: o.description ?? undefined })),
    ];
    const picked = typeof value === "string" ? def.enumOptions?.find((o) => o.value === value) : undefined;
    return (
      <label className="field">
        {label}
        <Select<string>
          value={typeof value === "string" ? value : ""}
          options={options}
          onChange={(v) => onChange(v || null)}
          disabled={disabled}
          ariaLabel={def.label}
        />
        {picked?.description && (
          <small className="muted enum-description" role="note">{picked.description}</small>
        )}
        {errorNode}
      </label>
    );
  }

  const withUnit = (input: JSX.Element) =>
    def.unit ? (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{input}</div>
        <span className="muted" style={{ fontSize: 12.5, whiteSpace: "nowrap" }}>{def.unit}</span>
      </div>
    ) : input;

  if (def.dataType === "NUMBER") {
    return (
      <label className="field">
        {label}
        {withUnit(
          <input
            className={inputClass}
            type="number"
            step="any"
            inputMode="decimal"
            value={typeof value === "number" ? value : ""}
            onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
            disabled={disabled}
          />,
        )}
        {errorNode}
      </label>
    );
  }

  if (def.dataType === "RANGE") {
    // Two numbers, Min and Max (sprint 5f §7); one end may stay empty, both empty = no value.
    const range: RangeValue = isRangeValue(value) ? value : { min: null, max: null };
    const set = (end: "min" | "max", raw: string) => {
      const n = raw === "" ? null : Number(raw);
      const next = { ...range, [end]: n === null || Number.isNaN(n) ? null : n };
      onChange(next.min === null && next.max === null ? null : next);
    };
    const end = (which: "min" | "max") => (
      <input
        className={inputClass}
        type="number"
        step="any"
        inputMode="decimal"
        value={range[which] ?? ""}
        onChange={(e) => set(which, e.target.value)}
        disabled={disabled}
        placeholder={which === "min" ? t("attributes.range.min", "Min") : t("attributes.range.max", "Max")}
        aria-label={which === "min" ? t("attributes.range.minOf", "{{label}} – min", { label: def.label }) : t("attributes.range.maxOf", "{{label}} – max", { label: def.label })}
      />
    );
    return (
      <div className="field">
        {label}
        {withUnit(
          <div className="range-inputs">
            {end("min")}
            <span className="muted" aria-hidden="true">–</span>
            {end("max")}
          </div>,
        )}
        {errorNode}
      </div>
    );
  }

  if (def.dataType === "DATE") {
    return (
      <label className="field">
        {label}
        <DateInput
          value={typeof value === "string" ? value : ""}
          onChange={(v) => onChange(v || null)}
          disabled={disabled}
          ariaLabel={def.label}
        />
        {errorNode}
      </label>
    );
  }

  return (
    <label className="field">
      {label}
      {withUnit(
        <input
          className={inputClass}
          value={typeof value === "string" ? value : value === null ? "" : String(value)}
          onChange={(e) => onChange(e.target.value || null)}
          disabled={disabled}
        />,
      )}
      {errorNode}
    </label>
  );
}
